import React from 'react';
import {
  User, Mail, Building, Phone, FileText, MoreVertical,
  LockOpen,
} from "lucide-react";
import DataTable from 'react-data-table-component';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { DataStyles } from '../Common/DataTableStyles';

const PublishersTable = ({
  publishers,
  loading,
  searchTerm,
  onSearch,
  onToggleInvoice,
  onApprovePublisher,
  onDeletePublisher,
  onSelectPublisher,
  isAdmin,
}) => {
  const headers = [
    { label: "No", icon: "", selector: "no" },
    { label: "Name", icon: <User size={16} />, selector: "name" },
    { label: "Email", icon: <Mail size={16} />, selector: "email" },
    { label: "Company", icon: <Building size={16} />, selector: "company" },
    { label: "Phone", icon: <Phone size={16} />, selector: "phone" },
    { label: "Password", icon: <LockOpen size={16} />, selector: "password" },
    { label: "Invoice Enabled", icon: <FileText size={16} />, selector: "isInvoiceEnable" },
    { label: "Actions", icon: <MoreVertical size={16} />, selector: "" },
  ];

  const columns = headers.map(({ label, icon, selector }) => {
    if (label === "Actions") {
      return {
        name: (
          <div className="flex items-center text-gray-600 font-semibold">
            {icon}
            <span className="ml-2">{label}</span>
          </div>
        ),
        cell: (publisher) => (
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => onToggleInvoice(publisher.id, publisher.isInvoiceEnable)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition ${
                publisher.isInvoiceEnable
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              {publisher.isInvoiceEnable ? 'Disable Invoice' : 'Enable Invoice'}
            </button>

            {isAdmin && !publisher.isApprovedByAdmin && (
              <button
                onClick={() => onApprovePublisher(publisher.id)}
                className="px-3 py-1 rounded-md text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition"
              >
                Approve Publisher
              </button>
            )}

            <button
              onClick={() => onSelectPublisher(publisher)}
              className="px-3 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition"
            >
              View Details
            </button>

            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this publisher?')) {
                  onDeletePublisher(publisher.id);
                }
              }}
              className="px-3 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 transition"
            >
              Delete
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      };
    } else {
      return {
        name: (
          <div className="flex items-center text-gray-600 font-semibold">
            {icon}
            <span className="ml-2">{label}</span>
          </div>
        ),
        selector: (row, index) => {
          if (selector === "no") return index + 1;
          if (selector === "isInvoiceEnable") {
            return row[selector]
              ? <FaCheckCircle className="text-green-500" />
              : <FaTimesCircle className="text-red-500" />;
          }
          return row[selector]?.toString() ?? "";
        },
        sortable: true,
        wrap: true,
      };
    }
  });

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
        <h2 className="text-xl font-semibold text-gray-800">Publishers Management</h2>
        <div className="relative w-full md:w-72">
          <input
            type="text"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none focus:border-blue-500 text-sm"
            placeholder="Search publishers..."
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={publishers}
          pagination
          highlightOnHover
          striped
          responsive
          persistTableHead
          noDataComponent={<div className="py-6 text-gray-500">No publishers found.</div>}
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 15, 20]}
          customStyles={DataStyles}
          className="border border-gray-200 rounded-lg overflow-hidden"
        />
      )}
    </div>
  );
};

export default PublishersTable;
